import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/shared/decorators';
import { generateMessage as genMessage } from '@app/text-content/messages/message-generator';
import { TranslateService } from '@ngx-translate/core';
import {
  chain,
  compact,
  concat,
  forEach,
  get,
  has,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isPlainObject,
  isString,
  map,
  merge,
  omit,
  reduce,
  set,
  trim,
  uniq,
  unset,
} from 'lodash';
import { ColDef } from 'ag-grid-community';
import {
  ARRAY_OF_OBJECT_PATH_SEPARATOR,
  DEFAULT_LOCALE,
  MAX_CLONE_DEPTH,
  NESTED_FIELD_SEPARATOR,
  ORIGINAL_VALUE_FIELD,
  TRANSLATION_OBJECT_FIELD,
} from '@config/global-constants';
import { II18nConfig, ServiceIdentifyType } from '../base/service-config.type';
import { cloneDataWithDepth } from '@app/shared/utils/common-utils';
import {
  IPreparePathsForArray,
  IPreparePathsForSimpleField,
  UserDefinedTranslationApplyingOptions,
  UserDefinedTranslationRevertOptions,
} from '@app/i18n/i18n.types';
import { I18nUserDefinedConfig } from '@app/i18n/i18n-user-defined.types';
import {
  customEntityTypeMapper,
  getUserDefinedConfig,
  getUserDefineStringPaths,
} from '@app/i18n/user-defined-strings.helper';
import { LocaleService } from './locale.service';
import { IBaseEntityType } from '@app/shared/types/entities/base.type';

@Injectable({ providedIn: 'root' })
export class I18nService {
  @lazyInject(TranslateService)
  public translateService: TranslateService;

  @lazyInject(LocaleService) public localeService: LocaleService;

  public static instance: I18nService;
  public static preventGlobalChange = false;

  public constructor(protected injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public static makeClientDataTranslationCode(params: {
    entityId: string;
    propertyName: string;
  }): string {
    return `${params.entityId}__${params.propertyName}`;
  }

  public static makeClientDataNestedTranslationId(params: {
    entityId: string;
    propertyName: string;
  }): string {
    return `${params.entityId}.${params.propertyName}`;
  }

  public generateMessage(
    msgTemplate: string,
    params?: Record<string, string | number>,
  ): string {
    return genMessage(msgTemplate, params);
  }

  public instant(key: string | Array<string>, interpolateParams?: Object) {
    if (isNil(key) || !isString(key) || isEmpty(key)) {
      return key;
    }

    return this.translateService.instant.apply(this.translateService, [
      key,
      interpolateParams,
    ]);
  }

  /**
   * alias of instant method
   */
  public translate = this.instant;

  /**
   * alias of instant method
   */
  public t = this.instant;

  public translateHeaderNames(headerNames: ColDef[]): ColDef[] {
    return map(headerNames, (item) => ({
      ...item,
      headerName: this.instant(item?.headerName),
    }));
  }

  public removeI18nField<T extends object = object>(doc: T): T {
    if (isArray(doc)) {
      return map(doc, (item) => this.removeI18nField(item)) as T;
    } else if (isPlainObject(doc)) {
      return reduce(
        doc,
        (acc, value, key) => {
          if (isEqual(key, TRANSLATION_OBJECT_FIELD)) {
            return acc;
          }

          return {
            ...acc,
            [key]:
              isPlainObject(value) || isArray(value)
                ? this.removeI18nField(value as unknown as object)
                : value,
          };
        },
        {} as T,
      );
    }

    return doc;
  }

  public applyUserDefinedTranslations<T extends any>(
    identify: ServiceIdentifyType,
    body: T,
    i18nConfig?: II18nConfig,
  ): T {
    if (has(body, 'items') && isArray((body as any).items)) {
      (body as any).items = map((body as any).items, (item) =>
        this.applyItemUserDefinedTranslations(identify, item, i18nConfig),
      );

      return body;
    }

    return this.applyItemUserDefinedTranslations(
      identify,
      body as any,
      i18nConfig,
    );
  }

  public applyItemUserDefinedTranslations<T extends object = IBaseEntityType>(
    identify: ServiceIdentifyType,
    doc: T,
    i18nConfig?: II18nConfig,
  ): T {
    const defaultI18nConfig = {
      isDocumentForEditor: false,
    };

    const mergedI18nConfig = merge({}, defaultI18nConfig, i18nConfig);

    const { isDocumentForEditor = false } = mergedI18nConfig;

    const { locale, paths } = this.getUserDefinedConfigToProcess(identify);

    if (!locale || isEmpty(paths)) return doc;

    const clonedData = cloneDataWithDepth(doc);
    const translatedDoc = reduce<string, T>(
      paths,
      (document: T, path: string): T => {
        try {
          const applyParams: UserDefinedTranslationApplyingOptions<T> = {
            document,
            locale,
            path,
            isDocumentForEditor,
            skipNonExistedFields: true,
          };

          return includes(path, ARRAY_OF_OBJECT_PATH_SEPARATOR)
            ? this.applyUserDefinedTranslationsForArrayOfObjects(applyParams)
            : this.applyUserDefinedTranslationsForSimpleField(applyParams);
        } catch (error) {
          console.error(`Failed to apply translation for path: ${path}`, error);

          return document; // Return document unchanged on error
        }
      },
      clonedData,
    );

    return translatedDoc;
  }

  public getUserDefinedConfigToProcess(
    identify: ServiceIdentifyType,
    i18nConfig?: II18nConfig,
  ): {
    paths?: string[];
    identify?: ServiceIdentifyType;
    locale?: string;
    userDefinedConfig?: I18nUserDefinedConfig;
  } {
    const locale = this.localeService.userLocale;

    if (!locale) return {};
    const mappedIdentify = customEntityTypeMapper(identify);

    return {
      locale,
      identify: mappedIdentify,
      paths: [...getUserDefineStringPaths(mappedIdentify, i18nConfig)],
      userDefinedConfig: getUserDefinedConfig(mappedIdentify, i18nConfig),
    };
  }

  public applyUserDefinedTranslationsForArrayOfObjects<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    isDocumentForEditor,
    skipNonExistedFields,
  }: UserDefinedTranslationApplyingOptions<T>): T {
    const {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
    } =
      this.preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(path);

    const clearedPathToArrayString = clearedPathToArray.join('.');
    const currentArrayValue = get(document, clearedPathToArray, []);
    if (isEmpty(currentArrayValue)) return document;
    const translatedArray = map(currentArrayValue, (currentObject, index) => {
      if (isEmpty(omit(currentObject, [TRANSLATION_OBJECT_FIELD]))) return null;

      if (!isEmpty(nestedPaths)) {
        const clearedNestedPaths = compact(
          nestedPaths.join(`.${ARRAY_OF_OBJECT_PATH_SEPARATOR}.`).split('.'),
        );
        const documentForRecursiveCall = get(document, [
          ...clearedPathToArray,
          `${index}`,
        ]);
        const newPathForRecursiveCall = [
          ...clearedNestedPaths,
          ARRAY_OF_OBJECT_PATH_SEPARATOR,
          ...clearedPathInObject,
        ].join('.');

        return this.applyUserDefinedTranslationsForArrayOfObjects({
          locale,
          isDocumentForEditor,
          skipNonExistedFields,
          document: documentForRecursiveCall,
          path: newPathForRecursiveCall,
        });
      }

      // just skip if no translation target field is provided
      if (skipNonExistedFields && !has(currentObject, clearedPathInObject)) {
        return currentObject;
      }

      const currentObjectValue = get(currentObject, clearedPathInObject, null);

      if (
        has(currentObject, pathToStoreOriginalFieldValue) &&
        !isDocumentForEditor
      ) {
        return currentObject;
      }

      const { pathToStoreWithLocale: pathToTranslatedValue } =
        this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
          clearedPathInObject.join('.'),
          locale,
        );

      const translatedObjectValue = this.getTranslationForObjectValue(
        currentObject,
        currentObjectValue,
        pathToStoreOriginalFieldValue,
        pathToTranslatedValue,
      );

      if (
        !isDocumentForEditor ||
        !has(currentObject, pathToStoreOriginalFieldValue)
      ) {
        set(
          currentObject,
          pathToStoreOriginalFieldValue,
          isEmpty(currentObjectValue) ? null : currentObjectValue,
        );
      }

      return set(currentObject, clearedPathInObject, translatedObjectValue);
    });

    const compactedArray = compact(translatedArray);

    if (compactedArray.length !== translatedArray.length) {
      if (compactedArray.length === 0) {
        unset(document, [clearedPathToArrayString]);

        return document;
      }
      if (isDocumentForEditor) {
        console.error(
          'Array size changes on translation. Some Elements were skipped due to emptiness',
        );
      }
    }

    return set(document, clearedPathToArray, translatedArray);
  }

  public applyUserDefinedTranslationsForSimpleField<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    isDocumentForEditor,
    skipNonExistedFields,
  }: UserDefinedTranslationApplyingOptions<T>): T {
    const {
      pathToField,
      fieldName,
      pathToStoreWithLocale: pathToTranslation,
    } = this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
      path,
      locale,
    );
    const pathToStoreOriginalFieldValue = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      ORIGINAL_VALUE_FIELD,
      fieldName,
    ]);

    const currentFieldValue = get(document, path, null);
    if (
      (skipNonExistedFields && !has(document, path)) ||
      (has(document, pathToStoreOriginalFieldValue) && !isDocumentForEditor)
    ) {
      return document;
    }

    let noTranslationLabel;
    if (isDocumentForEditor) {
      noTranslationLabel = '';
    } else {
      noTranslationLabel =
        this.buildNonTranslatedReadableValue(currentFieldValue);
    }
    const translatedValue = get(
      document,
      pathToTranslation,
      noTranslationLabel,
    );
    if (!has(document, pathToStoreOriginalFieldValue)) {
      set(
        document,
        pathToStoreOriginalFieldValue,
        isEmpty(currentFieldValue) ? null : currentFieldValue,
      );
    }

    return set(document, path, translatedValue);
  }

  public preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(
    originalPath: string,
    locale: string = null,
  ): IPreparePathsForArray {
    const defaultLocale = this.localeService.userLocale;

    const pathsToTheCurrentField = originalPath.split(
      `.${ARRAY_OF_OBJECT_PATH_SEPARATOR}.`,
    );
    const pathInObject = pathsToTheCurrentField.pop();
    const [closesDocumentInheritancePath, ...nestedPaths] =
      pathsToTheCurrentField;
    const clearedPathToArray = compact(
      closesDocumentInheritancePath.split('.'),
    );
    const clearedPathInObject = compact(pathInObject.split('.'));
    const {
      pathToField: pathToFieldInObject,
      pathToStoreWithLocale: pathToStoreOriginalFieldValue,
    } = this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
      pathInObject,
      ORIGINAL_VALUE_FIELD,
    );
    const { pathToStoreWithLocale } =
      this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
        pathInObject,
        locale ?? defaultLocale,
      );

    return {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
      pathToStoreWithLocale,
      pathToFieldInObject,
    };
  }

  public preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
    originalPath: string,
    locale?: string,
  ): IPreparePathsForSimpleField {
    const pathToField = compact([...originalPath.split('.')]);
    const fieldName = pathToField.pop();
    const pathToStoreWithLocale = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      locale,
      fieldName,
    ]);

    return {
      pathToField,
      fieldName,
      pathToStoreWithLocale,
    };
  }

  public buildNonTranslatedReadableValue(originalValue: string): string {
    if (!this.isNeedUserDefinedTranslate() || isEmpty(originalValue)) {
      return originalValue;
    }

    return isEmpty(originalValue)
      ? originalValue
      : `${originalValue} [${DEFAULT_LOCALE}]`;
  }

  public isNeedUserDefinedTranslate(): boolean {
    return this.localeService.isNeedUserDefinedTranslate();
  }

  public getTranslationForObjectValue(
    obj: object,
    objValue: string,
    pathToOriginalValue: string[],
    pathToTranslatedValue: string[],
  ) {
    const isTranslationAlreadyApplied = has(obj, pathToOriginalValue);
    if (isTranslationAlreadyApplied) return objValue;

    const isTranslationPresent = has(obj, pathToTranslatedValue);
    if (isTranslationPresent) return get(obj, pathToTranslatedValue);

    return this.buildNonTranslatedReadableValue(objValue);
  }

  public revertUserDefinedTranslations<
    T extends IBaseEntityType | { items: IBaseEntityType[] },
  >(doc: T, identify: ServiceIdentifyType, i18nConfig?: II18nConfig): T {
    const defaultPathPrefix =
      has(doc, 'items') && isArray(doc?.items)
        ? ['items', ARRAY_OF_OBJECT_PATH_SEPARATOR]
        : '';

    return this.revertSimpleUserDefinedTranslations(
      doc,
      identify,
      merge({}, <II18nConfig>{ pathPrefix: defaultPathPrefix }, i18nConfig),
    );
  }

  public revertSimpleUserDefinedTranslations<
    T extends object = IBaseEntityType,
  >(doc: T, identify: ServiceIdentifyType, i18nConfig?: II18nConfig): T {
    const { locale, paths } = this.getUserDefinedConfigToProcess(
      identify,
      i18nConfig,
    );
    if (!locale || isEmpty(paths)) return doc;

    const clonedData = cloneDataWithDepth(doc, MAX_CLONE_DEPTH);

    return reduce(
      paths,
      (document: T, path: string): T => {
        const revertParams: UserDefinedTranslationRevertOptions<T> = {
          document,
          locale,
          path,
          skipNonExistedFields: !get(clonedData, 'is_master'),
        };

        const revertedResult = includes(path, ARRAY_OF_OBJECT_PATH_SEPARATOR)
          ? this.revertUserDefinedTranslationsForArrayOfObjects(revertParams)
          : this.revertUserDefinedTranslationsForSimpleField(revertParams);

        return revertedResult;
      },
      clonedData,
    );
  }

  private revertUserDefinedTranslationsForSimpleField<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    originalDocument,
    skipNonExistedFields,
  }: UserDefinedTranslationRevertOptions<T>): T {
    const { pathToField, fieldName, pathToStoreWithLocale } =
      this.preparePathsForUserDefinedTranslationsForSimpleFieldsMapper(
        path,
        locale,
      );
    const pathToOriginalValue = compact([
      ...pathToField,
      TRANSLATION_OBJECT_FIELD,
      ORIGINAL_VALUE_FIELD,
      fieldName,
    ]);
    if (skipNonExistedFields && !has(document, path)) {
      unset(document, pathToOriginalValue);

      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }
    const documentWithOriginal =
      isEmpty(originalDocument) || has(document, pathToOriginalValue)
        ? document
        : originalDocument;

    const translatedValue = trim(get(document, path));
    const originalValue = get(documentWithOriginal, pathToOriginalValue);
    if (
      isEmpty(translatedValue) ||
      this.buildNonTranslatedReadableValue(originalValue) === translatedValue
    ) {
      unset(document, pathToStoreWithLocale);
    } else {
      set(document, pathToStoreWithLocale, translatedValue);
    }

    if (!has(documentWithOriginal, pathToOriginalValue)) {
      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }

    if (isNil(originalValue)) {
      unset(document, path);
      unset(document, pathToOriginalValue);

      return this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      });
    }

    unset(document, pathToOriginalValue);

    return set(
      this.clearI18nObjectIfEmpty(document, {
        pathToField,
        unsetIfEmpty: false,
      }),
      path,
      originalValue,
    );
  }

  private revertUserDefinedTranslationsForArrayOfObjects<
    T extends object = IBaseEntityType,
  >({
    document,
    locale,
    path,
    skipNonExistedFields,
  }: UserDefinedTranslationRevertOptions<T>): T {
    const {
      clearedPathToArray,
      clearedPathInObject,
      nestedPaths,
      pathToStoreOriginalFieldValue,
      pathToStoreWithLocale,
      pathToFieldInObject,
    } = this.preparePathsForUserDefinedTranslationsForArrayOfObjectsMapper(
      path,
      locale,
    );

    const currentArrayValue = get(document, clearedPathToArray, []);
    if (isEmpty(currentArrayValue)) return document;
    const revertedArray = map(currentArrayValue, (currentObject, index) => {
      const clonedObject = cloneDataWithDepth(currentObject);

      if (!isEmpty(nestedPaths)) {
        // after decomposing pathsToTheCurrentField nestedPaths may have the structure like that
        // ['.nested_level_1.'] or ['.nested_level_1.', '.nested_level_2.']
        const clearedNestedPaths = chain(nestedPaths)
          .map((nestedPath) => trim(nestedPath, NESTED_FIELD_SEPARATOR))
          .join(
            `${NESTED_FIELD_SEPARATOR}${ARRAY_OF_OBJECT_PATH_SEPARATOR}${NESTED_FIELD_SEPARATOR}`,
          )
          .split(NESTED_FIELD_SEPARATOR)
          .compact()
          .value();

        const documentForRecursiveCall = get(document, [
          ...clearedPathToArray,
          `${index}`,
        ]);
        const newPathForRecursiveCall = [
          ...clearedNestedPaths,
          ARRAY_OF_OBJECT_PATH_SEPARATOR,
          ...clearedPathInObject,
        ].join(NESTED_FIELD_SEPARATOR);

        return this.revertUserDefinedTranslationsForArrayOfObjects({
          locale,
          document: documentForRecursiveCall,
          path: newPathForRecursiveCall,
        });
      }

      /**
       * will skip processing if the field to clear does not exist in the object
       * this is needed to avoid creating fields during revert process
       */
      if (skipNonExistedFields && !has(clonedObject, clearedPathInObject)) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      if (
        has(clonedObject, clearedPathInObject) &&
        !has(clonedObject, pathToStoreOriginalFieldValue)
      ) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      const translatedValue = trim(get(clonedObject, clearedPathInObject));

      if (isEmpty(translatedValue)) unset(clonedObject, pathToStoreWithLocale);
      else {
        if (has(clonedObject, pathToStoreWithLocale)) {
          set(clonedObject, pathToStoreWithLocale, translatedValue);
        }
      }
      if (!has(clonedObject, pathToStoreOriginalFieldValue)) {
        return this.clearI18nObjectIfEmpty(clonedObject, {
          pathToField: pathToFieldInObject,
          pathsToClear: [locale],
        });
      }

      const originalObjectValue = get(
        clonedObject,
        pathToStoreOriginalFieldValue,
      );
      unset(clonedObject, pathToStoreOriginalFieldValue);
      if (isNil(originalObjectValue)) unset(clonedObject, clearedPathInObject);
      else set(clonedObject, clearedPathInObject, originalObjectValue);

      return this.clearI18nObjectIfEmpty(clonedObject, {
        pathToField: pathToFieldInObject,
        pathsToClear: [locale],
      });
    });

    return set(document, clearedPathToArray, revertedArray);
  }

  private clearI18nObjectIfEmpty<T extends object>(
    obj: T,
    options?: {
      pathToField?: string[];
      unsetIfEmpty?: boolean;
      pathsToClear?: string[];
    },
  ): T {
    const { pathToField, unsetIfEmpty, pathsToClear } = merge(
      { pathToField: [], unsetIfEmpty: true, pathsToClear: [] },
      options,
    );

    const defaultClearPaths = [ORIGINAL_VALUE_FIELD];

    forEach(
      compact(uniq(concat(defaultClearPaths, pathsToClear))),
      (locale) => {
        const fieldToOriginalObject = [
          ...pathToField,
          TRANSLATION_OBJECT_FIELD,
          locale,
        ];
        const remainingTranslations = get(obj, fieldToOriginalObject, {});
        if (isEmpty(remainingTranslations)) unset(obj, fieldToOriginalObject);
      },
    );
    const pathToTranslationObject = [...pathToField, TRANSLATION_OBJECT_FIELD];
    if (
      has(obj, pathToTranslationObject) &&
      isEmpty(get(obj, pathToTranslationObject)) &&
      unsetIfEmpty
    ) {
      unset(obj, pathToTranslationObject);
    }

    return obj;
  }
}
